/**
 * Centralized public URL — used in share/refer/email links and OG tags.
 * One env var to flip the moment sonspeak.com DNS lands.
 */
export function publicUrl() {
  return process.env.REACT_APP_PUBLIC_URL || window.location.origin;
}

/**
 * Lightweight SEO helper — sets <title> + <meta description> + OG tags
 * on a page mount. No new dependency. Restores the previous title on
 * unmount so page changes don't leak titles.
 */
import { useEffect } from "react";

function upsertMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSEO({ title, description, noindex = false, path = "" } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = `${title} · SonSpeak`;

    upsertMeta("description", description);
    upsertMeta("og:title", title ? `${title} · SonSpeak` : "SonSpeak", "property");
    upsertMeta("og:description", description, "property");
    upsertMeta("og:type", "website", "property");

    const url = path ? `${publicUrl()}${path}` : window.location.href;
    upsertMeta("og:url", url, "property");
    upsertCanonical(url);

    // Robots: family profile pages opt out of indexing per privacy plan
    let robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    } else if (robots) {
      robots.setAttribute("content", "index, follow");
    }

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, noindex, path]);
}
